/**
 * Purpose:
 *   Renders a simple menu item link (non-expandable).
 */

import Link from 'next/link';
import { queueNextHistoryState } from '@/lib/navigation/historyState';
import { cx } from '@/util/functions/cx';
import { useMenuItem } from './(hooks)/useMenuItem';
import { useCollapsedMenuPopover } from './(hooks)/useCollapsedMenuPopover';
import { useMenuRoutePrefetch } from './(hooks)/useMenuRoutePrefetch';
import { CollapsedMenuItemPopover } from './CollapsedMenuItemPopover';
import { MenuItemIcon } from './MenuItemIcon';
import { MenuItemText } from './MenuItemText';
import { MENU_ITEM_BASE, MENU_ITEM_ACTIVE, MENU_TEXT, MENU_TEXT_ACTIVE } from './Menu.styles';
import type { MenuItemConfig } from '@/types/menu';

export interface ISimpleMenuItemProps {
  item: MenuItemConfig;
  isActive: boolean;
  isCollapsed: boolean;
  onNavigate: (href: string) => void;
  onLinkClick?: () => void;
}

export function SimpleMenuItem({
  item,
  isActive,
  isCollapsed,
  onNavigate,
  onLinkClick,
}: ISimpleMenuItemProps) {
  const prefetchRouteOnIntent = useMenuRoutePrefetch();
  const {
    popoverRef,
    isOpen,
    canPortal,
    position,
    setAnchorElement,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  } = useCollapsedMenuPopover();
  const { containerStyle, textStyle, iconContainerStyle } = useMenuItem({
    item,
    isExpanded: false,
    isActive,
    isParentActive: false,
    isCollapsed,
  });

  const textClasses = isActive ? MENU_TEXT_ACTIVE : MENU_TEXT;
  const itemClasses = cx(MENU_ITEM_BASE, isActive && MENU_ITEM_ACTIVE);

  if (!item.href) return null;
  const href = item.href;

  return (
    <>
      <Link
        ref={setAnchorElement}
        href={href}
        prefetch={false}
        onMouseEnter={() => {
          prefetchRouteOnIntent(href);
          if (isCollapsed) {
            handleMouseEnter();
          }
        }}
        onMouseLeave={isCollapsed ? handleMouseLeave : undefined}
        onFocus={() => {
          prefetchRouteOnIntent(href);
          if (isCollapsed) {
            handleFocus();
          }
        }}
        onBlur={isCollapsed ? handleBlur : undefined}
        onClick={() => {
          queueNextHistoryState(item.nextHistoryState);
          onNavigate(href);
          onLinkClick?.();
        }}
        data-testid={`menu-item-${item.id}`}
        className={itemClasses}
        style={containerStyle}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
      >
        <MenuItemIcon icon={item.icon} style={iconContainerStyle} />
        <MenuItemText text={item.label} className={textClasses} style={textStyle} />
      </Link>
      {isCollapsed ? (
        <CollapsedMenuItemPopover
          item={item}
          isOpen={isOpen}
          canPortal={canPortal}
          position={position}
          popoverRef={popoverRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isChildActive={() => isActive}
          onNavigate={onNavigate}
          onLinkClick={onLinkClick}
        />
      ) : null}
    </>
  );
}